import { isNullish } from '../';
import type { Entries, Nullish } from '../../types/utility';

const EN_US = 'en-US';

const NUMBER_OPTIONS = {
  ToNearestThousandth: { style: 'decimal', maximumFractionDigits: 3 },
  ToNearestThousandthFixed: {
    style: 'decimal',
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  },
  ToNearestHundredth: { style: 'decimal', maximumFractionDigits: 2 },
  ToNearestHundredthFixed: { style: 'decimal', maximumFractionDigits: 2, minimumFractionDigits: 2 },
  ToNearestTenth: { style: 'decimal', maximumFractionDigits: 1 },
  ToNearestTenthFixed: { style: 'decimal', maximumFractionDigits: 1, minimumFractionDigits: 1 },
  ToNearestWholeNumber: { style: 'decimal', maximumFractionDigits: 0 },
};

const NUMBER_FORMATTERS = (Object.entries(NUMBER_OPTIONS) as Entries<typeof NUMBER_OPTIONS>).reduce(
  (formats, [format, options]) => {
    return {
      ...formats,
      [format]: new Intl.NumberFormat(EN_US, options as Intl.NumberFormatOptions),
    };
  },
  {} as Record<keyof typeof NUMBER_OPTIONS, Intl.NumberFormat>,
);

/**
 *
 * @param {number} value The number to format.
 * @param {object|string} [options='ToNearestThousandth'] An `Intl.NumberFormatOptions` object or predefined format option; if providing an object, the `style` key (non-overridable) defaults to 'decimal'
 *
 * @example // Format option `string` examples:
 * formatNumber(1.2445, 'ToNearestThousandth') => '1.245'
 * formatNumber(1.25, 'ToNearestThousandthFixed') => '1.250'
 *
 * @returns The formatted number
 */
export function formatNumber(
  value: number | Nullish,
  options: Intl.NumberFormatOptions | keyof typeof NUMBER_FORMATTERS = 'ToNearestThousandth',
) {
  if (isNullish(value) || isNaN(value)) {
    return '';
  }

  const formatter = typeof options === 'string' ? NUMBER_FORMATTERS[options] : null;

  if (formatter) {
    return formatter.format(value);
  }

  if (!!options && typeof options === 'object') {
    // ensure `style: 'decimal'` is non-overridable
    return value.toLocaleString(EN_US, { ...options, style: 'decimal' });
  }

  return NUMBER_FORMATTERS.ToNearestThousandth.format(value);
}
